import { API } from '@neurotrack/web-core'
import { v4 as uuid } from 'uuid'
import merge from 'lodash-es/merge'
import { getExamType, getExamCode } from '@/util/examUtils'
import ntData from '@/util/ntData'

export const LOAD_ESS_DATA = 'LOAD_ESS_DATA'
export const EXAM_IS_LOADING = 'EXAM_IS_LOADING'
export const IMAGES_PRELOADED = 'IMAGES_PRELOADED'
export const CACHE_IMAGES = 'CACHE_IMAGES'
export const SET_PERCEPTION_ID = 'SET_PERCEPTION_ID'
export const TRACK_EVENT = 'TRACK_EVENT'
export const RESET_EXAM = 'RESET_EXAM'
export const SET_EXAM = 'SET_EXAM'
export const NEXT_TRIAL = 'NEXT_TRIAL'
export const PREV_TRIAL = 'PREV_TRIAL'
export const RESET_PERCEPTION_DATA = 'RESET_PERCEPTION_DATA'

/**
 * @param ess
 */
function interpolateImageURLs(ess) {
  const flattenedOutput = ntData.flatten(ess)
  return ntData
    .uniq(
      Object.keys(ntData.flatten(ess))
        .filter(item => item.includes('filename'))
        .map(item => flattenedOutput[item])
    )
    .map(imagePath => `${ess.base_media_url}/${imagePath}`)
}

/**
 * @param ess
 */
export function processEssData(ess) {
  return {
    ...ess,
    phases: ess.phases.map(phase => ({
      ...phase,
      id: `phase-${uuid()}`
    }))
  }
}

/**
 * @param ess
 */
export function sanitizeEssData(ess) {
  const phases = ess.phases.map(phase => {
    const { phase_configuration } = phase
    const trials = phase.trials.map(trial => merge({}, phase_configuration, trial))

    return {
      ...phase,
      trials
    }
  })

  return {
    ...ess,
    phases
  }
}

export default {
  namespaced: true,
  state: () => ({
    base_media_url: null,
    status: null,
    phases: [],
    isLoading: false,
    preloadable_images: [],
    imageCache: {},
    images_preloaded: false,
    type: null,
    routes: [],
    assessment_id: '',
    perception_id: '',
    phase: null,
    trial: null,
    tracking: []
  }),
  mutations: {
    [RESET_PERCEPTION_DATA](state) {
      console.info('...purging perception data')
      state.perception_id = ''
      state.tracking = []
    },
    [TRACK_EVENT](state, { data, phaseIndex, trialIndex }) {
      if (!state.tracking[phaseIndex]) {
        state.tracking[phaseIndex] = []
      }
      state.tracking[phaseIndex][trialIndex] = data
    },
    [RESET_EXAM](state) {
      state.phase = 0
      state.trial = 0
    },
    [SET_EXAM](state, { phase, trial }) {
      state.phase = phase
      state.trial = trial
    },
    [PREV_TRIAL](state) {
      let trialLength = state.routes[state.phase]
      let trial = state.trial - 1
      if (trial === -1) {
        state.phase -= 1
        trialLength = state.routes[state.phase - 1]
        trial = trialLength - 1
      }
      state.trial = trial
    },
    [NEXT_TRIAL](state) {
      const trialLength = state.routes[state.phase]
      let trial = state.trial + 1
      if (trial > trialLength - 1) {
        state.phase += 1
        trial = 0
      }
      state.trial = trial
    },
    [EXAM_IS_LOADING](state, isLoading) {
      state.isLoading = isLoading
    },
    [IMAGES_PRELOADED](state, loaded) {
      state.images_preloaded = loaded
    },
    [SET_PERCEPTION_ID](state, id) {
      state.perception_id = id
    },
    [LOAD_ESS_DATA](state, { essData, processed }) {
      state.phases = processed.phases
      state.status = processed.status
      state.type = processed.type
      state.base_media_url = processed.base_media_url
      state.assessment_id = processed.id
      state.preloadable_images = interpolateImageURLs(processed)
      state.imageCache = {}
      state.images_preloaded = false

      const routes = essData.phases.map(item => item.trials.length)

      state.routes = routes
    },
    [CACHE_IMAGES](state, resultsArray) {
      resultsArray.forEach(res => {
        state.imageCache[res.url] = res.data
      })
    }
  },
  getters: {
    hasPerceptionStarted: state => Boolean(state.perception_id),
    currentTrial: ({ phases, phase, trial }) => phases[phase]?.trials[trial]
  },
  actions: {
    prev({ commit }) {
      commit(PREV_TRIAL)
    },
    async next({
      state, commit, getters, dispatch
    }) {
      commit(NEXT_TRIAL)

      if (Number(state.phase) < state.phases.length
        && getters.currentTrial.requires_perception_data
        && !getters.hasPerceptionStarted
      ) {
        dispatch('initPerception')
      }
    },
    /**
     * @param root0
     * @param root0.state
     * @param root0.commit
     * @param payload
     * Future optimization - store a map of trials by trial ID for faster lookup
     */
    trackEvent({ state, commit }, payload) {
      // check to see if we actually need to track this event first
      const { phases } = state
      let trial
      let phaseIndex
      let trialIndex

      for (let i = 0; i < phases.length; i++) {
        const phase = phases[i]
        for (let j = 0; j < phase.trials.length; j++) {
          const currentTrial = phase.trials[j]
          if (currentTrial.exam_trial_id === payload.exam_trial_id) {
            phaseIndex = i
            trialIndex = j
            trial = currentTrial
            break
          }
        }

        if (trial) {
          break
        }
      }
      if (trial.requires_perception_data) {
        commit(TRACK_EVENT, { phaseIndex, trialIndex, data: payload })
      }
    },
    uploadExamVideo({ state, rootState }, { snippetId, clip, contentType }) {
      return new Promise((resolve, reject) => {
        API.exam.videoUpload(
          rootState.user.profile.user_id,
          state.assessment_id,
          snippetId,
          clip,
          contentType
        ).then(({ file }) => {
          resolve({
            snippetId,
            file
          })
        }).catch(reject)
      })
    },
    /**
     *
     * @param {*} param0 - Refine this type
     * @param {{ videos: Array, perceptions: Map<string, object> }} payload - Video payload
     */
    async complete(
      { state, dispatch },
      { videos, perceptions } = {}
    ) {
      if (videos) {
        try {
          const videoPromises = await Promise.all(videos)
          videoPromises.forEach(({ snippetId, file }) => {
            const perception = perceptions.get(snippetId)
            perception.data[0].value.file = file
            dispatch('trackEvent', perception)
          })
        } catch (e) {
          console.error('Error while uploading videos...', e)
        }
      }
      dispatch('addPerceptionEvents', {
        assessment_id: state.assessment_id,
        perceptions: state.tracking
          .filter(phase => typeof phase === 'object')
          .flat()
      })
    },
    initPerception({ state, commit }) {
      commit(RESET_PERCEPTION_DATA)
      commit(SET_PERCEPTION_ID, state.assessment_id)
    },
    async addPerceptionEvents({ state, dispatch, rootState }, payload) {
      await API.exam.createPerceptionItems(
        rootState.user.profile.user_id,
        state.assessment_id,
        payload
      )

      dispatch('finishPerception')
    },
    async finishPerception({ state, rootState }, reason) {
      if (state.assessment_id && state.assessment_id) {
        await API.exam.stopPerceptionEvent(
          rootState.user.profile.user_id,
          state.assessment_id,
          state.perception_id,
          reason || 'COMPLETE'
        )
      } else {
        console.log('could not call stopPerception, missing info')
      }
    },
    async preloadImages({ state, commit }) {
      if (!state.images_preloaded) {
        const imagePromises = []
        state.preloadable_images.forEach(url => {
          imagePromises.push(
            new Promise((resolve, reject) => {
              API.exam
                .getExamMedia(url)
                .then(data => resolve({
                  url,
                  data
                }))
                .catch(reject)
            })
          )
        })
        await Promise.all(imagePromises)
          .then(results => {
            commit(CACHE_IMAGES, results)
            commit(IMAGES_PRELOADED, true)
          })
          .catch(() => commit(IMAGES_PRELOADED, false))
          .finally(() => console.info(
            `preload attempted on ${state.preloadable_images.length} images`
          ))
      }
    },
    async fetchExam({
      commit, dispatch, rootState
    }, examName) {
      commit(EXAM_IS_LOADING, true)

      const examType = getExamType(examName)
      if (!examType) return

      const examCode = getExamCode(examType)
      if (!examCode) return

      const params = { exam_code: examCode, platform: 'WEB' }

      const essData = await API.admin.createAssessment(
        rootState.user.profile.user_id,
        params
      )

      const sanitized = sanitizeEssData(essData)
      const processed = processEssData(sanitized)

      commit(LOAD_ESS_DATA, { essData, processed })
      commit('exam/UPDATE_MEDIA_URL', processed.base_media_url, { root: true })
      dispatch('preloadImages')
      /**
       * If we're mocking a new test with fixture data, we can add
       * `mocked: true` to the JSON so that we don't get 401s on
       * endpoints (which now causes a redirect back to the
       * dashboard).
       */
      // if (!essData.mocked) {
      //   await dispatch('initPerception')
      // }
      commit(EXAM_IS_LOADING, false)
    }
  }
}
